import PropTypes from 'prop-types'
import { memo, useCallback, useMemo, useState } from 'react'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import Button from 'components/core/Button/Button'
import Form from 'components/core/Form/Form'
import Icon from 'components/core/Icon/Icon'
import TransitionSwap from 'components/elements/TransitionSwap/TransitionSwap'
import classNames from 'lib/util/classNames'
import styles from './Hero.module.scss'

/**
 * The `Hero`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Hero (props) {
  const { className } = props
  const { setIsCartOpen } = useRootContext()
  const {
    cartCount,
    clearCart,
    isCartReady,
    preferredCurrency,
    setPreferredCurrency
  } = useUserContext()
  const [value, setValue] = useState(preferredCurrency)
  const hasCart = useMemo(() => !!cartCount, [cartCount])
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.container,
        isCartReady && styles.ready,
        hasCart && styles.hasCart
      ]),
    [className, hasCart]
  )
  const prettyCount = useMemo(() => cartCount.toLocaleString(), [cartCount])

  /**
   * Handle click close
   */
  const handleClickClose = useCallback(() => {
    setIsCartOpen(false)
  }, [])

  /**
   * Handle change of currency
   */
  const handleChangeCurrency = useCallback(nextValue => {
    setValue(nextValue)
    setPreferredCurrency(nextValue)
  }, [])

  return (
    <div className={classNameOutput}>
      <div className={styles.background} />
      <div className={styles.header}>
        <Form>
          <Form.RadioTabs
            value={value}
            options={[
              {
                label: (
                  <div className={styles.tab}>
                    <span>$</span> USD
                  </div>
                ),
                value: 'USD',
                ariaLabel: 'Currency: USD',
                useTooltip: true
              },
              {
                label: (
                  <div className={styles.tab}>
                    <Icon icon="matic" /> POL
                  </div>
                ),
                value: 'MATIC',
                ariaLabel: 'Currency: POL',
                useTooltip: true
              }
            ]}
            onChange={handleChangeCurrency}
          />
        </Form>
        <>
          <Button
            className={styles.buttonArrow}
            type="symbol"
            icon="arrow_right"
            color="c"
            size="standard"
            useTooltip={true}
            title="Close"
            onClick={handleClickClose}
          />
          <Button
            className={styles.buttonClose}
            type="symbol"
            icon="close"
            color="c"
            size="standard"
            useTooltip={true}
            title="Close"
            onClick={handleClickClose}
          />
        </>
      </div>
      <div className={styles.footer}>
        <div className={styles.title}>
          <h1>Cart</h1>
          <div className={styles.badge}>
            <TransitionSwap>{prettyCount}</TransitionSwap>
          </div>
        </div>
        <div className={styles.clear}>
          <Button
            color="j"
            size="sm"
            icon="clear"
            side="left"
            borderRadius="c"
            useTooltip={true}
            title="Clear Your Cart"
            onClick={clearCart}
          >
            Clear All
          </Button>
        </div>
      </div>
    </div>
  )
}

/** @type {object} */
Hero.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
Hero.defaultProps = {
  className: ''
}

// Memoize
export default memo(Hero)
